import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2, AlertTriangle } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DocumentoContrato } from "@/components/contratos/DocumentoContrato";
import { FirmaClienteForm } from "@/components/contratos/FirmaClienteForm";
import { fechaHora } from "@/components/contratos/constants";

const pub = axios.create({ baseURL: `${API}/contratos/firma` });

// Cabecera con la marca de la empresa que envía el contrato (sin NuvliPro).
export const Marco = ({ children, empresa, logo, etiqueta = "Firma de contrato" }) => (
  <div className="min-h-screen bg-[#F4F7FA] font-['Montserrat',sans-serif]">
    <header className="bg-[#1B2B4B] border-b-4 border-[#00B4CC]">
      <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {logo && <span className="h-9 max-w-[130px] rounded-lg bg-white px-2 flex items-center justify-center shrink-0"><img src={logo} alt={`Logo de ${empresa}`} className="max-h-6 max-w-full object-contain" data-testid="firma-logo" /></span>}
          <span className="text-white font-bold truncate" data-testid="firma-empresa">{empresa || etiqueta}</span>
        </div>
        <span className="text-xs text-white/70 shrink-0">Firma electrónica</span>
      </div>
    </header>
    <main className="max-w-4xl mx-auto px-3 sm:px-4 py-5 sm:py-8 space-y-5">{children}</main>
  </div>
);

export async function descargarPublico(url, nombre) {
  const { data } = await axios.get(url, { responseType: "blob" });
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(data), download: nombre });
  document.body.appendChild(a); a.click(); a.remove();
}

export default function FirmarContrato() {
  const { token } = useParams();
  const [v, setV] = useState(null);
  const [error, setError] = useState("");
  const cargar = useCallback(() => pub.get(`/${token}`).then((r) => setV(r.data))
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Enlace no válido")), [token]);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { if (v) document.title = `Contrato ${v.numero} · ${v.empresa}`; }, [v]);

  const firmar = async (payload) => {
    try { await pub.post(`/${token}`, payload); toast.success("Contrato firmado. Te hemos enviado una copia por email."); await cargar(); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); if (e.response?.status === 409) cargar(); }
  };
  const pdf = () => descargarPublico(`${API}/contratos/firma/${token}/pdf`, `contrato_${v.numero}.pdf`).catch(() => toast.error("No se pudo descargar el PDF"));

  if (error) return <Marco><div className="rounded-2xl bg-white border border-slate-200 p-8 text-center" data-testid="firma-error"><AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" /><p className="font-semibold text-[#1B2B4B]">{error}</p></div></Marco>;
  if (!v) return <Marco><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00B4CC]" /></div></Marco>;
  const firmado = v.estado === "firmado";
  return (
    <Marco empresa={v.empresa} logo={v.logo}>
      <div className={`rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 ${firmado ? "bg-emerald-50 border border-emerald-200" : "bg-white border border-slate-200"}`} data-testid="firma-estado">
        {firmado ? <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" /> : null}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[#1B2B4B]">{firmado ? "Contrato firmado" : `${v.empresa} te ha enviado este contrato para firmar`}</p>
          <p className="text-sm text-slate-600">{firmado ? `Firmado el ${fechaHora(v.firmado_at)}. Puedes descargar tu copia en PDF.` : "Léelo con calma. Al final encontrarás el recuadro para firmar."}</p>
        </div>
        <Button variant="outline" onClick={pdf} className="shrink-0" data-testid="firma-descargar-pdf"><Download className="w-4 h-4 mr-1.5" /> Descargar PDF</Button>
      </div>
      <DocumentoContrato v={v} />
      {!firmado && <FirmaClienteForm v={v} onFirmar={firmar} />}
    </Marco>
  );
}
